/**
 * Invoice PDF Creator - Converted to use @react-pdf/renderer
 *
 * This component has been converted from the old jsPDF-based PDF generation
 * to use the new @react-pdf/renderer library, following the same pattern as
 * table-pdf-component.tsx and pending-pericias-pdf.
 *
 * Changes made:
 * - Replaced jsPDF with @react-pdf/renderer
 * - Created InvoicePDFDocument component using Document/Page/View/Text
 * - Replaced manual positioning with react-pdf styling
 * - Used PDFDownloadLink approach with frozen props for data safety
 * - Applied professional layout with borders and proper typography
 *
 * Note: @react-pdf/renderer package is already installed
 */

import React, { useMemo } from "react";
import { Document, Page, Text, View, StyleSheet, Font, Image, Link } from "@react-pdf/renderer";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { LoadingButton } from "@mui/lab";
import { getTranslation } from "../../../../shared/locale/locale";
import { formatCurrency } from "../../../../shared/lib/numbers";
import { InvoiceItem, InvoiceLineItem } from "../../../../entities/invoice/api/get-invoice-by-id";
import { getPericiaCustomPrice } from "../../../../entities/pericia";

const nuovautoIMG = require("../../../../assets/nuovauto.png");

// Register font for better typography
Font.register({
  family: "Helvetica",
  src: "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.53/fonts/Helvetica.ttf"
});

interface GenerateInvoicePdfDTO {
  costumerName: string;
  costumerAddress: string;
  costumerLanguage: string;
  isCostumerCompany: boolean;
  costumerCar: {
    model: string;
    brand: string;
    color: string;
    chassisNumber: string;
    insuranceName: string;
    plate: string;
    mileage: number | null;
  } | null;
  createdAt: string;
  totalPrice: string;
  IVA: number;
  totalIVA: string;
  items: InvoiceItem[];
  lines: InvoiceLineItem[];
  invoiceNumber: number;
  displayTotalHours: boolean;
  invoiceText: string;
  insuranceClaimNumber: string;
}

// PDF styles using service orders layout
const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 9,
    padding: 20,
    backgroundColor: "#ffffff",
    color: "#000",
    lineHeight: 1.4,
    display: "flex",
    flexDirection: "column",
    height: "100%"
  },
  pageContent: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column"
  },
  header: {
    marginBottom: 5,
    marginLeft: 10,
    marginRight: 10
  },
  logo: {
    width: 180,
    height: 60,
    alignSelf: "center",
    marginVertical: 0
  },
  flexRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    marginLeft: 10,
    marginRight: 10
  },
  section: {
    marginBottom: 10,
    marginLeft: 10,
    marginRight: 10
  },
  boldText: {
    fontWeight: "bold"
  },
  infoBlocksContainer: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    borderTopWidth: 1,
    borderColor: "black",
    marginTop: 0,
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0
  },
  infoBlocksContainerSpacer: {
    marginBottom: 10
  },
  infoBlock: {
    flexBasis: "25%",
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRightWidth: 1,
    borderColor: "black",
    justifyContent: "center",
    alignItems: "flex-start"
  },
  infoBlockSmall: {
    flexBasis: "20%",
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRightWidth: 1,
    borderColor: "black",
    justifyContent: "center",
    alignItems: "flex-start"
  },
  infoBlockMedium: {
    flexBasis: "30%",
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRightWidth: 1,
    borderColor: "black",
    justifyContent: "center",
    alignItems: "flex-start"
  },
  infoLabel: {
    fontSize: 7,
    color: "#555"
  },
  infoValue: {
    fontSize: 9,
    fontWeight: "bold",
    textAlign: "left"
  },
  infoValueSmallFontSize: {
    fontSize: 8,
    fontWeight: "bold",
    textAlign: "left"
  },
  itemBlock: {
    marginBottom: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 10
  },
  itemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 10,
    marginBottom: 5,
    borderColor: "black",
    paddingBottom: 2,
    fontWeight: "bold"
  },
  itemDescription: {
    flexGrow: 1,
    textAlign: "left"
  },
  itemPlate: {
    width: 90,
    textAlign: "left"
  },
  itemChassis: {
    width: 120,
    textAlign: "left"
  },
  itemMileage: {
    width: 60,
    textAlign: "right"
  },
  itemHours: {
    width: 60,
    textAlign: "right"
  },
  itemPrice: {
    width: 80,
    textAlign: "right"
  },
  borderedSection: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "black",
    marginLeft: 10,
    marginRight: 10,
    padding: 0,
    paddingBottom: 5,
    marginBottom: 15,
    minHeight: 450, // Fixed minimum height
    flexGrow: 1, // Take available space
    display: "flex",
    flexDirection: "column"
  },
  itemsContainer: {
    flexGrow: 1,
    minHeight: 300 // Minimum space for items
  },
  totalContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: "auto", // Push to bottom of bordered section
    paddingRight: 10,
    borderColor: "black",
    paddingTop: 5
  },
  totalText: {
    fontSize: 12,
    fontWeight: "bold"
  },
  link: {
    color: "blue",
    textDecoration: "underline"
  },
  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-around",
    marginTop: 30,
    marginLeft: 10,
    marginRight: 10
  },
  signatureBox: {
    alignItems: "center"
  },
  signatureLine: {
    width: 150,
    borderBottom: 1,
    marginBottom: 4
  },
  footerSection: {
    position: "absolute",
    bottom: 20,
    left: 30,
    fontSize: 8,
    color: "black"
  },
  footerText: {
    marginBottom: 2,
    fontWeight: "bold"
  },
  messagesSection: {
    marginTop: "auto", // Push to bottom of available space
    marginBottom: 60, // Space for footer
    marginLeft: 10,
    marginRight: 10
  }
});

// PDF Document Component - Service Orders Layout Style
const InvoicePDFDocument: React.FC<{ props: GenerateInvoicePdfDTO }> = ({ props }) => {
  const {
    costumerName,
    costumerAddress,
    isCostumerCompany,
    costumerCar,
    createdAt,
    invoiceNumber,
    costumerLanguage,
    IVA,
    invoiceText,
    items = [],
    lines = [],
    displayTotalHours = false,
    insuranceClaimNumber
  } = props;
  console.log("showing:", isCostumerCompany);

  const text = getTranslation(costumerLanguage);

  // Process customer address
  const costumerAddress1 = costumerAddress.split(",").slice(0, 2).join(" ");
  const costumerAddress2 = costumerAddress.split(",").slice(2).join(" ");

  // Calculate totals
  let totalHoursCount = 0;
  let finalPrice = 0;

  // Calculate final totals for all items (pericias)
  items.forEach((item) => {
    const { pericia } = item;
    const totalHours = (pericia.insuranceHours > 0 ? pericia.insuranceHours : pericia.totalHours) / 10;
    totalHoursCount += totalHours;
    const itemPrice = getPericiaCustomPrice(pericia);
    finalPrice += itemPrice;
  });

  // Add line costs to final price
  lines.forEach((line) => {
    finalPrice += line.cost || 0;
  });

  const ivaValue = (finalPrice * IVA) / 100;
  const totalWithIva = finalPrice + ivaValue;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.pageContent}>
          {/* Header with invoice number */}
          <View style={styles.header}>
            <Text>
              {text.invoice_pdf.header.invoice_number}: 0{invoiceNumber}
            </Text>
          </View>

          <Image src={nuovautoIMG} style={styles.logo} />

          {/* Customer & Company Information */}
          <View style={styles.flexRow}>
            <View>
              <Text>{costumerName}</Text>
              <Text>{costumerAddress1}</Text>
              <Text>{costumerAddress2}</Text>
            </View>
            <View>
              <Text style={styles.boldText}>NUOVAUTO di Keil Roger</Text>
              <Text>Via San Gottardo 22/A</Text>
              <Text>6532 Arbedo-Castione</Text>
              <Link src="http://www.nuovauto.ch" style={styles.link}>
                www.nuovauto.ch
              </Link>
              <Link src="mailto:amministrazione@nuovauto.ch" style={styles.link}>
                amministrazione@nuovauto.ch
              </Link>
            </View>
          </View>

          {/* Invoice Items with fixed-height bordered section */}
          <View style={styles.borderedSection}>
            <View style={styles.infoBlocksContainer}>
              <View style={styles.infoBlock}>
                <Text style={styles.infoLabel}>{text.invoice_pdf.header.invoice_number}</Text>
                <Text style={styles.infoValue}>0{invoiceNumber || "-"}</Text>
              </View>
              <View style={styles.infoBlock}>
                <Text style={styles.infoLabel}>{text.invoice_pdf.header.client_name}</Text>
                <Text style={styles.infoValue}>{costumerName || "-"}</Text>
              </View>
              <View style={styles.infoBlock}>
                <Text style={styles.infoLabel}>{text.invoice_pdf.header.invoice_date}</Text>
                <Text style={styles.infoValue}>{createdAt || "-"}</Text>
              </View>
              <View style={styles.infoBlock}>
                {!isCostumerCompany && (
                  <>
                    <Text style={styles.infoLabel}>{text.invoice_pdf.header.insurance_claim_number}</Text>
                    <Text style={styles.infoValue}>{insuranceClaimNumber || "-"}</Text>
                  </>
                )}
              </View>
            </View>
            {!isCostumerCompany && (
              <View style={styles.infoBlocksContainer}>
                <View style={styles.infoBlock}>
                  <Text style={styles.infoLabel}>{"Marca"}</Text>
                  <Text style={styles.infoValue}>{costumerCar?.brand ?? "-"}</Text>
                </View>
                <View style={styles.infoBlock}>
                  <Text style={styles.infoLabel}>{"Modelo"}</Text>
                  <Text style={styles.infoValue}>{costumerCar?.model ?? "-"}</Text>
                </View>
                <View style={styles.infoBlockSmall}>
                  <Text style={styles.infoLabel}>{"Targa"}</Text>
                  <Text style={styles.infoValue}>{costumerCar?.plate.replaceAll(" ", "") ?? "-"}</Text>
                </View>
                <View style={styles.infoBlockMedium}>
                  <Text style={styles.infoLabel}>{"Telaio"}</Text>
                  <Text style={styles.infoValueSmallFontSize}>{costumerCar?.chassisNumber ?? "-"}</Text>
                </View>
                <View style={styles.infoBlock}>
                  <Text style={styles.infoLabel}>{"Colore"}</Text>
                  <Text style={styles.infoValue}>{costumerCar?.color ?? "-"}</Text>
                </View>
                <View style={styles.infoBlock}>
                  <Text style={styles.infoLabel}>{"Assicurazione"}</Text>
                  <Text style={styles.infoValue}>{costumerCar?.insuranceName ?? "-"}</Text>
                </View>
                <View style={styles.infoBlockSmall}>
                  <Text style={styles.infoLabel}>{"Km"}</Text>
                  <Text style={styles.infoValue}>{costumerCar?.mileage?.toLocaleString() ?? "-"}</Text>
                </View>
              </View>
            )}

            <View style={styles.infoBlocksContainerSpacer} />

            {/* Items Container with flexible height */}
            <View style={styles.itemsContainer}>
              {/* Items Header */}
              <View style={styles.itemHeader}>
                <Text style={styles.itemDescription}>{text.invoice_pdf.table.description}</Text>
                {isCostumerCompany && (
                  <>
                    <Text style={styles.itemPlate}>{text.invoice_pdf.table.plate}</Text>
                    <Text style={styles.itemChassis}>{text.invoice_pdf.table.chassis}</Text>
                  </>
                )}
                {displayTotalHours && <Text style={styles.itemHours}>{text.invoice_pdf.table.hours}</Text>}
                <Text style={styles.itemPrice}>{text.invoice_pdf.table.price}</Text>
              </View>

              {/* Items (Pericias) */}
              {items.map((item, index) => {
                const { pericia, car } = item;
                const { brand, model, plate, chassisNumber, mileage } = car;
                const chassis = chassisNumber.length > 2 ? chassisNumber : "-";
                const totalHours = (pericia.insuranceHours > 0 ? pericia.insuranceHours : pericia.totalHours) / 10;
                const itemPrice = getPericiaCustomPrice(pericia);

                return (
                  <View key={`item-${index}`} style={styles.itemBlock}>
                    <Text style={styles.itemDescription}>
                      {brand} {model}
                    </Text>
                    <Text style={styles.itemPlate}>{plate}</Text>
                    <Text style={styles.itemChassis}>{chassis}</Text>
                    {displayTotalHours && <Text style={styles.itemHours}>{totalHours.toFixed(2)}</Text>}
                    <Text style={styles.itemPrice}>{formatCurrency(itemPrice)}</Text>
                  </View>
                );
              })}

              {/* Lines (Service Order Lines) */}
              {lines.map((line, index) => {
                const description = line.description?.trim() ? line.description : "Servizio aggiuntivo";
                const lineCost = line.cost || 0;

                return (
                  <View key={`line-${index}`} style={styles.itemBlock}>
                    <Text style={styles.itemDescription}>{description}</Text>
                    {isCostumerCompany && (
                      <>
                        <Text style={styles.itemPlate}>-</Text>
                        <Text style={styles.itemChassis}>-</Text>
                        <Text style={styles.itemMileage}>-</Text>
                      </>
                    )}
                    {displayTotalHours && <Text style={styles.itemHours}>-</Text>}
                    <Text style={styles.itemPrice}>{formatCurrency(lineCost)}</Text>
                  </View>
                );
              })}

              {/* Invoice Text */}
              {invoiceText && (
                <View style={{ paddingHorizontal: 10, marginTop: 15 }}>
                  <Text>{invoiceText}</Text>
                </View>
              )}
            </View>

            {/* Costs Summary - positioned at bottom of bordered section */}
            <View style={styles.totalContainer}>
              <View>
                <Text>
                  {text.invoice_pdf.costs.subtotal}: {formatCurrency(finalPrice)}
                </Text>
                <Text>
                  IVA {IVA}%: {formatCurrency(ivaValue)}
                </Text>
                <Text style={styles.totalText}>
                  {text.invoice_pdf.costs.total}: {formatCurrency(totalWithIva)}
                </Text>
              </View>
            </View>
          </View>

          {/* Introduction/Closing message - positioned at bottom */}
          <View style={styles.messagesSection}>
            <Text>{text.invoice_pdf.introduction_message}</Text>
            <View style={{ marginTop: 10 }} />
            <Text>{text.invoice_pdf.salute_message}</Text>
          </View>
        </View>

        {/* Footer with payment info */}
        <View style={styles.footerSection}>
          <Text style={styles.footerText}>PostFinance</Text>
          <Text style={styles.footerText}>CH24 3000 0001 1554 7392 4</Text>
          <Text style={styles.footerText}>IVA: CHE-162.607.499</Text>
        </View>
      </Page>
    </Document>
  );
};

// Component for generating and downloading the PDF
interface InvoicePDFDownloadProps {
  props: GenerateInvoicePdfDTO;
  disabled?: boolean;
  loading?: boolean;
}

export const InvoicePDFDownload: React.FC<InvoicePDFDownloadProps> = ({ props, disabled = false, loading = false }) => {
  const fileName = `${props.costumerName}_fattura_0${props.invoiceNumber}.pdf`;
  console.log("InvoicePDFDownload rendered with:", { props });

  // Memoize the PDF document and create isolated copies to prevent external mutations
  const pdfDocument = useMemo(() => {
    // Create a frozen copy of the props to prevent external mutations during PDF generation
    const frozenProps: GenerateInvoicePdfDTO = {
      costumerName: props.costumerName,
      costumerAddress: props.costumerAddress,
      costumerLanguage: props.costumerLanguage,
      isCostumerCompany: props.isCostumerCompany,
      costumerCar: props.costumerCar ? { ...props.costumerCar } : null, // Shallow copy of car object
      createdAt: props.createdAt,
      totalPrice: props.totalPrice,
      IVA: props.IVA,
      totalIVA: props.totalIVA,
      items: [...props.items], // Shallow copy of array
      lines: props.lines ? [...props.lines] : [],
      invoiceNumber: props.invoiceNumber,
      displayTotalHours: props.displayTotalHours,
      invoiceText: props.invoiceText,
      insuranceClaimNumber: props.insuranceClaimNumber
    };

    return <InvoicePDFDocument props={frozenProps} />;
  }, [
    props.costumerName,
    props.costumerAddress,
    props.costumerLanguage,
    props.isCostumerCompany,
    props.costumerCar,
    props.createdAt,
    props.totalPrice,
    props.IVA,
    props.totalIVA,
    props.items,
    props.lines,
    props.invoiceNumber,
    props.displayTotalHours,
    props.invoiceText,
    props.insuranceClaimNumber
  ]);

  return (
    <PDFDownloadLink document={pdfDocument} fileName={fileName}>
      {({ loading: pdfLoading }: { loading: boolean }) => (
        <LoadingButton
          fullWidth
          variant="contained"
          sx={{ mt: 10, mb: 1 }}
          disabled={disabled || pdfLoading}
          loading={loading || pdfLoading}
        >
          {pdfLoading ? "Gerando PDF..." : "GERAR PDF"}
        </LoadingButton>
      )}
    </PDFDownloadLink>
  );
};

// Export the new React PDF version while maintaining the old interface
export const useCreateInvoicePDFReactPDF = {
  Component: InvoicePDFDownload
};
